import type { RouterOutputs } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { dayStatusLabel, type DayStatus } from "@shared/trustLabels";
import { useI18n } from "@/lib/i18n";

/**
 * Daily receipts list for the Drafts tab. Renders the SERVER-classified days
 * from `receipts.weekStatus` verbatim — it performs NO date bucketing, NO
 * timezone classification, NO status calculation, NO workday-denominator
 * computation. It shows the configured WORKING days (the server's
 * `isWorkingDay`) plus any weekend day that actually has a captured note
 * (`weekend_note`); empty rest days are not listed. Each row maps the server
 * `status` enum → a client-safe label + dot colour and offers the per-day
 * Draft / Review&edit / Approve actions.
 */
type WeekStatus = RouterOutputs["receipts"]["weekStatus"];
type DayCell = WeekStatus["days"][number];
type ForWeek = RouterOutputs["receipts"]["forWeek"];
type DailyReceipt = ForWeek["daily"][number];

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const WD_AR = ["الأحد", "الإثنين", "الثلاثاء", "الأربعاء", "الخميس", "الجمعة", "السبت"];

const DOT: Record<DayStatus, string> = {
  approved: "bg-emerald-500",
  draft: "bg-sky-500",
  ready: "bg-violet-500",
  update_available: "bg-amber-500",
  needs_update: "bg-orange-500",
  retracted: "bg-slate-400",
  not_started: "bg-muted-foreground/40",
  weekend_note: "bg-amber-400",
  hidden: "bg-transparent",
};

function fmtDate(dateKey: string): string {
  return new Date(dateKey + "T00:00:00.000Z").toLocaleDateString("en", {
    month: "short",
    day: "numeric",
    timeZone: "UTC",
  });
}

function Row({
  cell,
  daily,
  busy,
  onDraft,
  onReview,
  onApprove,
}: {
  cell: DayCell;
  daily?: DailyReceipt;
  busy?: boolean;
  onDraft: () => void;
  onReview: () => void;
  onApprove: () => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  const status = cell.status as DayStatus;
  const label = dayStatusLabel(status, locale);
  const approved = status === "approved";
  const updateAvailable = status === "update_available";
  const changesNotShared = status === "needs_update";
  const retracted = status === "retracted";
  const notStarted = status === "not_started";
  const reviewLabel = approved ? (isAr ? "تعديل" : "Edit") : isAr ? "مراجعة" : "Review";
  // Approve appears only when there's something to publish: a never-approved
  // draft, an unshared edited version, or a retracted receipt (Re-approve
  // republishes). Approved/clean and "new updates captured" show no Approve.
  const showApprove = !approved && !updateAvailable;
  const approveLabel =
    changesNotShared || retracted
      ? isAr
        ? "إعادة الاعتماد"
        : "Re-approve"
      : isAr
        ? "اعتماد"
        : "Approve";
  // Rest days appear only as read-only captured notes — they are not workday
  // receipts and offer no Draft/Approve actions.
  const isRest = !cell.isWorkingDay;
  const headline = (daily?.content as any)?.headline as string | undefined;

  return (
    <div className="flex items-center gap-3 py-3" data-testid="daily-receipt-row">
      <div className="flex flex-col items-center w-12 shrink-0">
        <div className="text-xs font-medium">{(isAr ? WD_AR : WD)[cell.weekdayIndex]}</div>
        <div className="text-[11px] text-muted-foreground">{fmtDate(cell.dateKey)}</div>
      </div>
      <div className="min-w-0 flex-1">
        {label ? (
          <div className="inline-flex items-center gap-1 text-[11px] text-muted-foreground">
            <span className={`h-1.5 w-1.5 rounded-full ${DOT[status]}`} />
            {label}
          </div>
        ) : null}
        <p className="text-sm text-foreground/90 truncate">
          {/* A not-started day shows NO content (any residual headline is
              stale) — but it DOES show waiting captures, so a captured day is
              distinguishable from an empty one. */}
          {notStarted
            ? cell.signalCount > 0
              ? isAr
                ? `${cell.signalCount === 1 ? "تحديث واحد مُلتقط" : `${cell.signalCount} تحديثات مُلتقطة`}. جاهز لإنشاء مسودة`
                : `${cell.signalCount} update${cell.signalCount === 1 ? "" : "s"} captured. Ready to draft`
              : isAr
                ? "لا تحديث بعد"
                : "No update yet"
            : headline || (isAr ? "المسودة جاهزة" : "Draft ready")}
        </p>
      </div>
      <div className="shrink-0 flex items-center gap-1.5">
        {isRest ? null : notStarted ? (
          <Button size="sm" onClick={onDraft} disabled={busy} className="rounded-full">
            {busy ? "…" : isAr ? "إنشاء مسودة" : "Draft"}
          </Button>
        ) : updateAvailable ? (
          // Re-draft to fold in the new capture, then open the editor.
          <Button size="sm" onClick={onDraft} disabled={busy} className="rounded-full">
            {busy ? "…" : isAr ? "مراجعة التحديثات" : "Review updates"}
          </Button>
        ) : (
          <>
            <Button size="sm" variant="outline" onClick={onReview} className="rounded-full">
              {reviewLabel}
            </Button>
            {showApprove ? (
              <Button size="sm" onClick={onApprove} className="rounded-full">
                {approveLabel}
              </Button>
            ) : null}
          </>
        )}
      </div>
    </div>
  );
}

export function DailyReceiptsList({
  weekStatus,
  dailyByKey,
  busyKey,
  isPersonal = false,
  onDraft,
  onReview,
  onApprove,
}: {
  weekStatus: WeekStatus;
  dailyByKey: Record<string, DailyReceipt>;
  busyKey?: string | null;
  /** Personal workspace → softer, non-work-framed denominator copy
   *  ("days" not "workdays"). Defaults to the team wording. */
  isPersonal?: boolean;
  onDraft: (dateKey: string) => void;
  onReview: (receipt: DailyReceipt) => void;
  onApprove: (receipt: DailyReceipt) => void;
}) {
  const { locale } = useI18n();
  const isAr = locale === "ar";
  // Working days always shown; rest days only if they carry a captured note.
  const rows = weekStatus.days.filter(
    d => d.isWorkingDay || d.status === "weekend_note",
  );

  return (
    <section
      className="rounded-2xl border border-border bg-card/60 p-4"
      data-testid="daily-receipts"
    >
      <div className="text-xs font-semibold tracking-wide text-muted-foreground mb-1">
        {isAr ? "الملخّصات اليومية" : "DAILY RECEIPTS"}
      </div>
      <div className="text-[11px] text-muted-foreground mb-1">
        {isAr
          ? `${weekStatus.approvedWorkdays} من ${weekStatus.workdayCount} ${isPersonal ? "أيام معتمدة" : "أيام عمل معتمدة"}`
          : `${weekStatus.approvedWorkdays} of ${weekStatus.workdayCount} ${isPersonal ? "days" : "workdays"} approved`}
      </div>
      <div className="divide-y divide-border">
        {rows.map(cell => {
          const daily = dailyByKey[cell.dateKey];
          return (
            <Row
              key={cell.dateKey}
              cell={cell}
              daily={daily}
              busy={busyKey === cell.dateKey}
              onDraft={() => onDraft(cell.dateKey)}
              onReview={() => daily && onReview(daily)}
              onApprove={() => daily && onApprove(daily)}
            />
          );
        })}
      </div>
    </section>
  );
}
